import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Flight,
  Map as MapIcon,
  Architecture,
  Route,
  Logout,
  ManageAccounts,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { ThemeToggle } from './ThemeToggle';
import { StatusIndicator } from './StatusIndicator';
import { userManager } from '../../auth/oidcConfig';
import { tutorialTarget } from '../../tutorial/tours';

/**
 * The app's pages. `map` is the live map, which shows drones in the air;
 * `fleet` is the gallery of every drone, parked ones included (#412, G9). The
 * map's tab was called Fleet until the gallery needed the name.
 */
export type AppView = 'map' | 'fleet' | 'planner' | 'tooling' | 'admin' | 'settings';

interface NavigationProps {
  currentView: AppView;
  onViewChange: (view: AppView) => void;
  /** Shows the admin tab. Visibility only — the API enforces the role itself. */
  showAdmin?: boolean;
}

export const Navigation: React.FC<NavigationProps> = ({
  currentView,
  onViewChange,
  showAdmin = false,
}) => {
  // On a phone the title alone is wider than the space left over once the tabs
  // and session controls have taken theirs, so it is dropped below sm and the
  // Flight icon carries the identity instead (#400).
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

  const onSignOut = async () => {
    try {
      await userManager.signoutRedirect();
    } catch {
      // Dex may not expose an end-session endpoint. End the local session
      // anyway — App listens for `userUnloaded` and returns to the login gate.
      await userManager.removeUser().catch(() => undefined);
    }
  };

  return (
    // `viewport-fit=cover` (#399) lets the page run under the notch, which in
    // landscape sits beside the bar. The insets are added to the AppBar, which
    // has no horizontal padding of its own, so the Toolbar's gutters still
    // apply on top and nothing shifts on a device without a notch.
    <AppBar
      position="static"
      elevation={1}
      sx={{
        pl: 'env(safe-area-inset-left, 0px)',
        pr: 'env(safe-area-inset-right, 0px)',
      }}
    >
      <Toolbar>
        <Flight sx={{ mr: 2, flexShrink: 0 }} />
        {!isNarrow && (
          <Typography variant="h6" component="div" noWrap sx={{ mr: 3 }}>
            Drone Fleet Monitor
          </Typography>
        )}
        {/* Scrollable so the tabs stay reachable once they outgrow the row,
            rather than overlapping the controls beside them (#400). */}
        <Tabs
          value={currentView}
          onChange={(_e, v: AppView) => onViewChange(v)}
          textColor="inherit"
          indicatorColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ flexGrow: 1, minWidth: 0 }}
          aria-label="Application view"
        >
          <Tab value="map" label="Map" icon={<MapIcon fontSize="small" />} iconPosition="start" />
          <Tab value="fleet" label="Fleet" icon={<Flight fontSize="small" />} iconPosition="start" />
          <Tab
            value="planner"
            label="Planner"
            icon={<Route fontSize="small" />}
            iconPosition="start"
            {...tutorialTarget('nav-planner')}
          />
          <Tab
            value="tooling"
            label="Tooling"
            icon={<Architecture fontSize="small" />}
            iconPosition="start"
          />
          {/* Rendering an unusable tab is worse than hiding it: every admin
              route 403s for an ordinary operator. */}
          {showAdmin && (
            <Tab
              value="admin"
              label="Users"
              icon={<ManageAccounts fontSize="small" />}
              iconPosition="start"
            />
          )}
          {/* Last, so its position does not shift when the admin tab appears
              for an admin and vanishes for everyone else (#378). */}
          <Tab
            value="settings"
            label="Settings"
            icon={<SettingsIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}
        >
          <StatusIndicator />
          <ThemeToggle />
          <Tooltip title="Sign out">
            <IconButton color="inherit" aria-label="Sign out" onClick={() => void onSignOut()}>
              <Logout />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
